import * as React from 'react'
import L from 'leaflet'
import { PenLine, Trash2 } from 'lucide-react'
import { BOUNDARY_MIN_VERTICES } from '@lib/dto/geo.dto'
import type { AreaDto } from '@lib/dto/area.dto'
import {
  BOUNDARY_PROBLEM_MESSAGES,
  ringsOverlap,
} from '@lib/validators/polygon.validator'
import { BoundaryLayer } from '@/map/BoundaryLayer'
import { BoundaryDraftBar } from '@/map/BoundaryDraftBar'
import { RegionLabelLayer } from '@/map/RegionLabelLayer'
import { regionColors, type RegionColor } from '@/map/regionColors'
import { REGION_BG, REGION_FILL, REGION_STROKE } from '@/map/regionClasses'
import {
  areaSnapshot,
  editQuestion,
  type ChangeQuestion,
} from '@/site-visits/siteRecordChanges'
import {
  useBoundaryDraft,
  type BoundaryDraft,
  type BoundaryMode,
} from '@/map/useBoundaryDraft'
import {
  useAddArea,
  useRemoveArea,
  useUpdateArea,
  useAreas,
} from '@/hooks/useAreas'
import { isApiError } from '@/helpers/apiClient'
import { toErrorMessage } from '@/helpers/logger'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

/**
 * The same sentence the API returns when a save is refused.
 *
 * The editor says it while the corner is still being dragged, so a person
 * finds out before they hit Save.
 */
function overlapMessage(name: string): string {
  return `This outline overlaps ${name}. Areas can meet along an edge, but they cannot overlap.`
}

/**
 * One area draft on the boundary screen.
 *
 * The page owns the map. This owns the ring being drawn, the saved polygons,
 * and the name. `paint` is what the page calls once the area layer exists;
 * later changes repaint themselves.
 */
export interface AreaEditor {
  draft: BoundaryDraft
  /** Leaflet handlers built once read the draft here. */
  draftRef: React.RefObject<BoundaryDraft>
  layerRef: React.RefObject<BoundaryLayer | null>
  paint: () => void
  /** Drop the saved polygons when the map is torn down. */
  detach: () => void
  drawing: boolean
  hint: string | null
  problem: string | null
  error: string | null
  areas: AreaDto[]
  /** Each area's color, by name: rings with one name share it. */
  colorOf: (area: AreaDto) => RegionColor
  areaName: string
  setAreaName: (name: string) => void
  nameSuggestions: string[]
  canSave: boolean
  busy: boolean
  trimmedName: string
  confirmingDelete: boolean
  setConfirmingDelete: (open: boolean) => void
  editingAreaId: string | null
  beginEdit: (area: AreaDto) => void
  beginNew: () => void
  discard: () => void
  /** With the answer, when `saveQuestion` asked one, and the day it changed. */
  save: (isCorrection?: boolean, changedOn?: string) => Promise<void>
  /**
   * What saving a redrawn area asks on a revisit, in place of Save; null
   * asks nothing. A new or renamed area never asks.
   */
  saveQuestion: ChangeQuestion | null
  remove: () => Promise<void>
}

/**
 * The areas on one site: the draft ring, the saved polygons, and the save.
 *
 * An area is a second use of the boundary draft, not a second copy of the
 * page. Saved rings live on the site. This draft is only the one being
 * drawn, so cancel returns to nothing. Opening an existing ring goes through
 * `startEditing(vertices)`, which adopts the ring and enters editing together.
 */
export function useAreaEditor(
  siteId: string,
  siteMode: BoundaryMode,
  mapRef: React.RefObject<L.Map | null>,
  isRevisit = false,
): AreaEditor {
  const areasQuery = useAreas(siteId)
  const addArea = useAddArea(siteId)
  const updateArea = useUpdateArea(siteId)
  const removeArea = useRemoveArea(siteId)
  const draft = useBoundaryDraft(null)
  const draftRef = React.useRef(draft)
  draftRef.current = draft
  const layerRef = React.useRef<BoundaryLayer | null>(null)
  const savedAreasRef = React.useRef<L.LayerGroup | null>(null)
  const labelsRef = React.useRef<RegionLabelLayer | null>(null)

  const [error, setError] = React.useState<string | null>(null)
  const [confirmingDelete, setConfirmingDelete] = React.useState(false)
  const [editingAreaId, setEditingAreaId] = React.useState<string | null>(null)
  const [areaName, setAreaName] = React.useState('')

  const areas = React.useMemo(
    () => areasQuery.data?.areas ?? [],
    [areasQuery.data],
  )
  const drawing = draft.mode !== 'view'
  /*
   * Neighbors never share a color, and rings with one name are one area
   * (docs/map-regions.md). Every area used to be the same blue, so where
   * one ended and the next began was a guess.
   */
  const colors = React.useMemo(
    () =>
      regionColors(
        areas.map((area) => ({ key: area.name, ring: area.boundary.vertices })),
      ),
    [areas],
  )
  const colorOf = React.useCallback(
    (area: AreaDto): RegionColor => colors.get(area.name) ?? 'yellow',
    [colors],
  )

  const overlapping = React.useMemo(() => {
    if (!drawing || draft.vertices.length < BOUNDARY_MIN_VERTICES) return null
    for (const area of areas) {
      if (area.id === editingAreaId) continue
      if (ringsOverlap(draft.vertices, area.boundary.vertices)) return area
    }
    return null
  }, [drawing, draft.vertices, areas, editingAreaId])

  const nameSuggestions = React.useMemo(() => {
    const names: string[] = []
    for (const area of areas) {
      if (area.id === editingAreaId) continue
      if (!names.includes(area.name)) names.push(area.name)
    }
    return names
  }, [areas, editingAreaId])

  const trimmedName = areaName.trim()
  const busy = addArea.isPending || updateArea.isPending || removeArea.isPending
  const canSave =
    draft.canSave &&
    trimmedName.length > 0 &&
    trimmedName.length <= 80 &&
    overlapping === null &&
    !busy

  /*
   * The draft layer follows every corner. The saved polygons do not: rebuilding
   * them on each drag would flicker the rings that are not being edited.
   * Both read this snapshot so the page can paint once, after the layer exists,
   * without the callbacks themselves changing identity.
   */
  const snapshotRef = React.useRef({
    overlapping,
    areas,
    colorOf,
    editingAreaId,
    siteMode,
  })
  snapshotRef.current = { overlapping, areas, colorOf, editingAreaId, siteMode }

  const renderDraft = React.useCallback(() => {
    const layer = layerRef.current
    if (layer === null) return
    const current = draftRef.current
    const overlap = snapshotRef.current.overlapping
    layer.render({
      vertices: current.vertices,
      editable: current.mode !== 'view',
      selectedIndex: current.selectedIndex,
      invalid: current.problem !== null || overlap !== null,
      closed: current.vertices.length >= 3,
      showMidpoints: current.mode === 'editing',
    })
  }, [])

  const renderSaved = React.useCallback(() => {
    const map = mapRef.current
    if (map === null) return
    const snapshot = snapshotRef.current
    const current = draftRef.current

    /*
     * Saved areas are static polygons. The one being edited is hidden,
     * because the draft layer is drawing it. Clicks reach them only while
     * nothing is being drawn, so a tap placing a corner is not swallowed
     * by an area already saved.
     */
    savedAreasRef.current?.remove()
    const interactive = current.mode === 'view' && snapshot.siteMode === 'view'
    const group = L.layerGroup()
    const shown = snapshot.areas.filter(
      (area) => area.id !== snapshot.editingAreaId || current.mode === 'view',
    )
    for (const area of shown) {
      const ring = area.boundary.vertices.map((vertex): L.LatLngTuple => [
        vertex.latitude,
        vertex.longitude,
      ])
      const color = snapshot.colorOf(area)
      const polygon = L.polygon(ring, {
        pane: 'site-areas',
        className: `${REGION_STROKE[color]} ${REGION_FILL[color]}`,
        weight: 2,
        fillOpacity: 0.22,
        interactive,
      })
      polygon.bindTooltip(area.name, { sticky: true, opacity: 1 })
      if (interactive) {
        polygon.on('click', (event: L.LeafletMouseEvent) => {
          L.DomEvent.stopPropagation(event)
          setError(null)
          setEditingAreaId(area.id)
          setAreaName(area.name)
          draftRef.current.startEditing(area.boundary.vertices)
        })
      }
      polygon.addTo(group)
    }
    group.addTo(map)
    savedAreasRef.current = group
    labelsRef.current ??= new RegionLabelLayer(map)
    labelsRef.current.set(
      shown.map((area) => ({
        key: area.id,
        ring: area.boundary.vertices,
        label: { kind: 'name', text: area.name },
      })),
    )
  }, [mapRef])

  const paint = React.useCallback(() => {
    renderDraft()
    renderSaved()
  }, [renderDraft, renderSaved])

  const detach = React.useCallback(() => {
    savedAreasRef.current?.remove()
    savedAreasRef.current = null
    labelsRef.current?.remove()
    labelsRef.current = null
    layerRef.current = null
  }, [])

  React.useEffect(() => {
    renderDraft()
  }, [
    renderDraft,
    draft.vertices,
    draft.mode,
    draft.selectedIndex,
    draft.problem,
    overlapping,
  ])

  React.useEffect(() => {
    renderSaved()
  }, [renderSaved, areas, colorOf, editingAreaId, draft.mode, siteMode])

  function beginEdit(area: AreaDto): void {
    setError(null)
    setEditingAreaId(area.id)
    setAreaName(area.name)
    draft.startEditing(area.boundary.vertices)
  }

  function beginNew(): void {
    setError(null)
    setEditingAreaId(null)
    setAreaName('')
    draft.startDrawing()
  }

  function discard(): void {
    setError(null)
    setConfirmingDelete(false)
    setEditingAreaId(null)
    setAreaName('')
    draftRef.current.cancel()
  }

  async function save(
    isCorrection?: boolean,
    changedOn?: string,
  ): Promise<void> {
    const boundary = draft.toSaveValue()
    const name = areaName.trim()
    if (boundary === null || name.length === 0 || overlapping !== null) return
    setError(null)
    try {
      if (editingAreaId === null) {
        await addArea.mutateAsync({ name, boundary })
      } else {
        await updateArea.mutateAsync({
          id: editingAreaId,
          input: {
            name,
            boundary,
            ...(isCorrection === undefined ? {} : { isCorrection }),
            ...(changedOn === undefined ? {} : { changedOn }),
          },
        })
      }
      discard()
    } catch (caught) {
      setError(
        isApiError(caught)
          ? (caught.body.message ?? 'Could not save the area.')
          : toErrorMessage(caught),
      )
    }
  }

  async function remove(): Promise<void> {
    if (editingAreaId === null) return
    setError(null)
    try {
      await removeArea.mutateAsync(editingAreaId)
      discard()
    } catch (caught) {
      setConfirmingDelete(false)
      setError(
        isApiError(caught)
          ? (caught.body.message ?? 'Could not delete the area.')
          : toErrorMessage(caught),
      )
    }
  }

  let problem: string | null = null
  if (drawing && draft.problem !== null) {
    problem = BOUNDARY_PROBLEM_MESSAGES[draft.problem]
  } else if (drawing && overlapping !== null) {
    problem = overlapMessage(overlapping.name)
  }

  const editing = areas.find((area) => area.id === editingAreaId)
  const redrawn = draft.toSaveValue()
  const saveQuestion =
    isRevisit && editing !== undefined && redrawn !== null
      ? editQuestion(
          'AREA',
          areaSnapshot(editing),
          areaSnapshot({ name: editing.name, boundary: redrawn }),
        )
      : null

  let hint: string | null = null
  if (drawing && draft.mode === 'drawing') {
    hint = 'Tap the map to trace this area'
  } else if (drawing) {
    hint = 'Drag a point to reshape the area · tap a faint point to add one'
  }

  return {
    draft,
    draftRef,
    layerRef,
    paint,
    detach,
    drawing,
    hint,
    problem,
    error,
    areas,
    colorOf,
    areaName,
    setAreaName,
    nameSuggestions,
    canSave,
    busy,
    trimmedName,
    confirmingDelete,
    setConfirmingDelete,
    editingAreaId,
    beginEdit,
    beginNew,
    discard,
    save,
    saveQuestion,
    remove,
  }
}

/** The chip row, or the name field and draft buttons while an area is open. */
export function AreaChrome({ editor }: { editor: AreaEditor }) {
  const listId = React.useId()
  if (!editor.drawing) {
    return (
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs text-muted-text">Areas</span>
        {editor.areas.map((area) => (
          <Button
            key={area.id}
            type="button"
            variant="outline"
            size="sm"
            className="h-9 max-w-40 lg:h-7"
            onClick={() => {
              editor.beginEdit(area)
            }}
          >
            {/* The area's color on the map, so a chip can be matched to its ring. */}
            <span
              aria-hidden="true"
              className={cn(
                'h-2.5 w-2.5 shrink-0 rounded-full',
                REGION_BG[editor.colorOf(area)],
              )}
            />
            <span className="truncate">{area.name}</span>
          </Button>
        ))}
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-9 lg:h-7"
          onClick={() => {
            editor.beginNew()
          }}
        >
          <PenLine className="h-3.5 w-3.5" />
          Draw area
        </Button>
      </div>
    )
  }

  const hasShape = editor.draft.vertices.length >= BOUNDARY_MIN_VERTICES
  return (
    <div className="flex w-full flex-col gap-2">
      <div>
        <label htmlFor={`${listId}-input`} className="text-xs text-muted-text">
          Area name
        </label>
        <Input
          id={`${listId}-input`}
          className="mt-1 h-11 lg:h-8"
          value={editor.areaName}
          maxLength={80}
          placeholder="Common Area"
          list={listId}
          onChange={(event) => {
            editor.setAreaName(event.target.value)
          }}
        />
        <datalist id={listId}>
          {editor.nameSuggestions.map((name) => (
            <option key={name} value={name} />
          ))}
        </datalist>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <BoundaryDraftBar
          draft={editor.draft}
          hasShape={hasShape}
          saveDisabled={!editor.canSave}
          pending={editor.busy}
          onDiscard={editor.discard}
          onSave={(isCorrection, changedOn) => {
            void editor.save(isCorrection, changedOn)
          }}
          question={editor.saveQuestion}
          // Drawn at a desk, in no visit: a redrawn ring says when too.
          askWhen
          extra={
            editor.editingAreaId === null ? null : (
              <Button
                type="button"
                variant="outline"
                className="h-11 text-danger lg:h-9"
                disabled={editor.busy}
                onClick={() => {
                  editor.setConfirmingDelete(true)
                }}
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            )
          }
        />
      </div>
      <Dialog
        open={editor.confirmingDelete}
        onOpenChange={editor.setConfirmingDelete}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              Delete{' '}
              {editor.trimmedName === '' ? 'this area' : editor.trimmedName}?
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-secondary-text">
            Trees in it stay on the site. A proposal already sent keeps the
            areas it went out with.
          </p>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                editor.setConfirmingDelete(false)
              }}
            >
              Keep area
            </Button>
            <Button
              variant="destructive"
              disabled={editor.busy}
              onClick={() => {
                void editor.remove()
              }}
            >
              Delete area
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
